import { FilterFacet } from '@apgnext/frontend/legacy/js/components/molecules/filter/types';
import { useEffect, useRef, useState } from 'react';

type UseModalOpts = {
  setActiveFilter: (f: FilterFacet | null) => void;
  setSortShown: (v: boolean) => void;
  enableClickOutside?: boolean;
  modalRef?: React.RefObject<HTMLElement>;
};

type AnyCloseEvent =
  | React.SyntheticEvent
  | React.MouseEvent
  | KeyboardEvent
  | Event
  | undefined;

export const useModal = ({ setActiveFilter, setSortShown, enableClickOutside = false, modalRef }: UseModalOpts) => {
  const activeModalButtonRef = useRef<HTMLElement | null>(null);

  const [modalPosition, setModalPosition] = useState<{ top: number; left: number } | null>(null);

  const closeModal = (event: AnyCloseEvent) => {
    event?.stopPropagation?.();
    setActiveFilter(null);
    setSortShown(false);
    activeModalButtonRef.current = null;
  };

  const initModalPosition = (event: React.MouseEvent, position: 'left' | 'right' = 'left') => {
    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();
    activeModalButtonRef.current = button;
    setModalPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const openFilterModal = (filter: FilterFacet, event: React.MouseEvent) => {
    event.stopPropagation();
    setSortShown(false);
    setActiveFilter(filter);
    initModalPosition(event);
  };

  const closeFilterModal = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSortShown(false);
    setActiveFilter(null);
    activeModalButtonRef.current = null;
  };

  const updateModalPosition = () => {
    if (!activeModalButtonRef.current) return;
    const rect = activeModalButtonRef.current.getBoundingClientRect();
    setModalPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  };

  const clickOutside = (e: Event) => {
    const target = e.target as Node | null;
    if (!target) return;

    if (modalRef?.current && modalRef.current.contains(target)) return;
    if (activeModalButtonRef.current?.contains(target)) return;

    closeModal(e);
  };

  useEffect(() => {
    const onScroll = () => updateModalPosition();
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, []);

  useEffect(() => {
    if (!enableClickOutside) return;

    const onClick = (e: MouseEvent) => {
      clickOutside(e);
    };

    document.addEventListener('click', onClick, { passive: true });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal(e);
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [enableClickOutside, modalRef?.current]);

  return {
    activeModalButtonRef,
    modalPosition,
    setModalPosition,
    closeModal,
    initModalPosition,
    openFilterModal,
    closeFilterModal,
    clickOutside,
  };
};
